import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  CircularProgress,
  Typography,
  Autocomplete,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  OpportunityDetails,
  OpportunityCategoryEnum,
  OpportunityConfidenceEnum,
  OpportunityStatusEnum,
  RaidCategoryEnum,
  RaidItemCreate,
  User,
  getUsers,
} from '../../services/api';
import {
  getDefaultExpectedDate,
  PartialRaidCommonProps,
} from './raidFormUtils';

interface OpportunityDialogProps extends PartialRaidCommonProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: RaidItemCreate) => void;
  initialData?: OpportunityDetails | null;
  itemId?: number | null;
  title?: string;
  readOnly?: boolean;
  isFromOtherQuestionnaire?: boolean;
}

const OpportunityDialog: React.FC<OpportunityDialogProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  itemId,
  title = '',
  readOnly = false,
  isFromOtherQuestionnaire = false,
  defaultAssigneeId,
  initialPartialRaid = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [partialRaid, setPartialRaid] = useState(false);
  // Partial RAID applies only on initial create, never when editing an existing item
  const isCreating = !itemId;
  const effectivePartialRaid = isCreating && partialRaid;
  const [formData, setFormData] = useState<OpportunityDetails>({
    description: '',
    category: [],
    expected_benefit: '',
    business_impact: '',
    estimated_value: undefined,
    confidence_level: OpportunityConfidenceEnum.MEDIUM,
    investment_required: '',
    person_responsible: '',
    expected_closure_date: '',
    actual_closure_date: '',
    status: OpportunityStatusEnum.IDENTIFIED,
    execution_plan: '',
  });
  const [formTitle, setFormTitle] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await getUsers();
        const filteredUsers = response.data.filter(
          (user: User) =>
            user.display_name !== 'System User' &&
            user.user_name !== 'system_user' &&
            user.status === 'ACTIVE'
        );
        setUsers(filteredUsers);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    if (open) {
      fetchUsers();
    }
  }, [open]);

  const findUserById = (userId: string): User | null => {
    if (!userId) return null;
    return users.find((u) => u.id.toString() === userId) || null;
  };

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        category: initialData.category || [],
        confidence_level:
          initialData.confidence_level || OpportunityConfidenceEnum.MEDIUM,
        status: initialData.status || OpportunityStatusEnum.IDENTIFIED,
      });
    } else {
      setFormData({
        description: '',
        category: [],
        expected_benefit: '',
        business_impact: '',
        estimated_value: undefined,
        confidence_level: OpportunityConfidenceEnum.MEDIUM,
        investment_required: '',
        person_responsible: '',
        expected_closure_date: '',
        actual_closure_date: '',
        status: OpportunityStatusEnum.IDENTIFIED,
        execution_plan: '',
      });
    }

    setFormTitle(title || '');
    setPartialRaid(itemId ? false : !!initialPartialRaid);
    setErrors({});
  }, [initialData, title, initialPartialRaid, open, itemId]);

  const applyPartialDefaults = () => {
    setFormData((prev) => ({
      ...prev,
      person_responsible:
        prev.person_responsible ||
        (defaultAssigneeId != null ? String(defaultAssigneeId) : ''),
      confidence_level:
        prev.confidence_level || OpportunityConfidenceEnum.MEDIUM,
      expected_closure_date:
        prev.expected_closure_date || getDefaultExpectedDate(),
    }));
  };

  const handlePartialRaidChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const enabled = event.target.checked;
    setPartialRaid(enabled);
    if (enabled && !itemId && !initialData) {
      applyPartialDefaults();
    }
  };

  const handleTextChange =
    (field: keyof OpportunityDetails) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: '' }));
      }
    };

  const handleSelectChange =
    (field: keyof OpportunityDetails) =>
    (event: React.ChangeEvent<{ value: unknown }>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: '' }));
      }
    };

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormTitle(event.target.value);
    if (errors.title) {
      setErrors((prev) => ({ ...prev, title: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formTitle.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.person_responsible?.trim()) {
      newErrors.person_responsible = 'Assigned To is required';
    }

    if (!effectivePartialRaid) {
      if (!formData.category || formData.category.length === 0) {
        newErrors.category = 'Category is required';
      }
      if (!formData.expected_benefit?.trim()) {
        newErrors.expected_benefit = 'Expected benefit is required';
      }
      if (!formData.business_impact?.trim()) {
        newErrors.business_impact = 'Business impact is required';
      }
      if (!formData.confidence_level) {
        newErrors.confidence_level = 'Confidence level is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const details: OpportunityDetails = {
        ...formData,
        confidence_level:
          formData.confidence_level || OpportunityConfidenceEnum.MEDIUM,
        expected_closure_date: formData.expected_closure_date || undefined,
        actual_closure_date: formData.actual_closure_date || undefined,
        estimated_value:
          formData.estimated_value !== undefined &&
          formData.estimated_value !== null &&
          String(formData.estimated_value) !== ''
            ? Number(formData.estimated_value)
            : undefined,
      };

      if (effectivePartialRaid) {
        details.confidence_level =
          details.confidence_level || OpportunityConfidenceEnum.MEDIUM;
        details.expected_closure_date =
          details.expected_closure_date || getDefaultExpectedDate();
      }

      const submitData: RaidItemCreate = {
        entity_id: 0,
        title: formTitle,
        raid_category: RaidCategoryEnum.OPPORTUNITY,
        partial_raid: effectivePartialRaid,
        details,
      };

      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('Error submitting opportunity item:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {readOnly
          ? 'View Opportunity'
          : itemId
            ? 'Update Opportunity'
            : 'Add New Opportunity'}
        {loading && (
          <CircularProgress
            size={20}
            sx={{ marginLeft: 2, verticalAlign: 'middle' }}
          />
        )}
      </DialogTitle>

      <DialogContent dividers>
        {readOnly && (
          <Typography color="info.main" variant="subtitle2" sx={{ mb: 2 }}>
            {isFromOtherQuestionnaire
              ? 'This item is from other reviews and is displayed in read-only mode.'
              : 'This review is submitted. RAID details are in read-only mode.'}
          </Typography>
        )}

        {isCreating && (
        <FormControlLabel
          control={
            <Switch
              checked={partialRaid}
              onChange={handlePartialRaidChange}
              disabled={readOnly}
            />
          }
          label="Partial RAID (show only required fields)"
          sx={{ mb: 1 }}
        />
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              label="Title"
              value={formTitle}
              onChange={handleTitleChange}
              fullWidth
              required
              error={!!errors.title}
              helperText={errors.title || 'Brief title describing the opportunity'}
              InputProps={{ readOnly }}
              disabled={readOnly}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Description"
              value={formData.description}
              onChange={handleTextChange('description')}
              multiline
              rows={3}
              fullWidth
              required
              error={!!errors.description}
              helperText={
                errors.description || 'What has been identified?'
              }
              InputProps={{ readOnly }}
              disabled={readOnly}
            />
          </Grid>

          {!effectivePartialRaid && (
            <>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  options={Object.values(OpportunityCategoryEnum)}
                  value={formData.category || []}
                  onChange={(_event, newValue) => {
                    setFormData((prev) => ({ ...prev, category: newValue }));
                    if (errors.category) {
                      setErrors((prev) => ({ ...prev, category: '' }));
                    }
                  }}
                  disabled={readOnly}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Category"
                      required={!effectivePartialRaid}
                      error={!!errors.category}
                      helperText={errors.category || 'Select one or more categories'}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Expected Benefit"
                  value={formData.expected_benefit || ''}
                  onChange={handleTextChange('expected_benefit')}
                  fullWidth
                  required={!effectivePartialRaid}
                  error={!!errors.expected_benefit}
                  helperText={
                    errors.expected_benefit ||
                    'What measurable value is expected?'
                  }
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Business Impact"
                  value={formData.business_impact || ''}
                  onChange={handleTextChange('business_impact')}
                  fullWidth
                  required={!effectivePartialRaid}
                  error={!!errors.business_impact}
                  helperText={
                    errors.business_impact ||
                    'Revenue, margin, cost, delivery, NPS, defects, risk'
                  }
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Estimated Value"
                  type="number"
                  value={
                    formData.estimated_value !== undefined &&
                    formData.estimated_value !== null
                      ? formData.estimated_value
                      : ''
                  }
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      estimated_value: val === '' ? undefined : Number(val),
                    }));
                  }}
                  fullWidth
                  helperText="Quantified benefit wherever possible"
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12} sm={6}>
            <FormControl
              fullWidth
              disabled={readOnly}
              required={!effectivePartialRaid}
              error={!!errors.confidence_level}
            >
              <InputLabel>Confidence Level</InputLabel>
              <Select
                value={formData.confidence_level || ''}
                label="Confidence Level"
                onChange={handleSelectChange('confidence_level') as any}
                readOnly={readOnly}
                inputProps={{ readOnly }}
              >
                {Object.values(OpportunityConfidenceEnum).map((level) => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {!effectivePartialRaid && (
            <Grid item xs={12}>
              <TextField
                label="Investment Required"
                value={formData.investment_required || ''}
                onChange={handleTextChange('investment_required')}
                fullWidth
                helperText="Cost, effort or approvals needed"
                InputProps={{ readOnly }}
                disabled={readOnly}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={users}
              getOptionLabel={(option) => option.display_name}
              value={findUserById(formData.person_responsible || '')}
              onChange={(_event, newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  person_responsible: newValue ? newValue.id.toString() : '',
                }));
                if (errors.person_responsible) {
                  setErrors((prev) => ({ ...prev, person_responsible: '' }));
                }
              }}
              disabled={readOnly}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assigned To"
                  required
                  error={!!errors.person_responsible}
                  helperText={
                    errors.person_responsible ||
                    'Person responsible for the opportunity'
                  }
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              noOptionsText="No users found"
            />
          </Grid>

          {!effectivePartialRaid && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth disabled={readOnly}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status || OpportunityStatusEnum.IDENTIFIED}
                  label="Status"
                  onChange={handleSelectChange('status') as any}
                  readOnly={readOnly}
                  inputProps={{ readOnly }}
                >
                  {Object.values(OpportunityStatusEnum).map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <TextField
              label="Expected Closure Date"
              type="date"
              value={formData.expected_closure_date || ''}
              onChange={handleTextChange('expected_closure_date')}
              InputLabelProps={{ shrink: true }}
              fullWidth
              InputProps={{ readOnly }}
              disabled={readOnly}
            />
          </Grid>

          {!effectivePartialRaid && (
            <>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Actual Closure Date"
                  type="date"
                  value={formData.actual_closure_date || ''}
                  onChange={handleTextChange('actual_closure_date')}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Execution Plan"
                  value={formData.execution_plan || ''}
                  onChange={handleTextChange('execution_plan')}
                  multiline
                  rows={3}
                  fullWidth
                  helperText="What must happen first"
                  InputProps={{ readOnly }}
                  disabled={readOnly}
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</Button>
        {!readOnly && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {itemId ? 'Update' : 'Save'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OpportunityDialog;
